// ViewerHeader.jsx
import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import logo from "../assets/logo.png";
import AttributionBox from "./AttributionBox";

const ViewerHeader = ({ system }) => {
  const headerRef = useRef(null);

  useEffect(() => {
    if (!headerRef.current) return;

    const updateHeaderHeight = () => {
      const height = headerRef.current.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        "--app-header-height",
        `${Math.ceil(height)}px`
      );
    };

    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(headerRef.current);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--app-header-height");
    };
  }, []);

  return (
    <Box>
      <Box
        className="viewer-header"
        ref={headerRef}
        sx={{
          position: "absolute",
          top: 0,
          left: "env(safe-area-inset-left, 0px)",
          right: "env(safe-area-inset-right, 0px)",
          width: "auto",
          color: "#fff",
          px: "var(--viewer-header-px, 16px)",
          py: "var(--viewer-header-py, 10px)",
          minHeight: "var(--viewer-header-height, 80px)",
          zIndex: 1000,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        <Box
          className="viewer-header__brand"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "clamp(8px, 1.2vw, 14px)",
            px: "clamp(10px, 1.4vw, 16px)",
            py: "clamp(7px, 1vw, 11px)",
            borderRadius: "var(--radius-lg, 14px)",
            backgroundColor: "var(--surface-bg, rgba(9, 12, 18, 0.62))",
            boxShadow: "var(--surface-shadow, 0 14px 34px rgba(0, 0, 0, 0.34))",
            backdropFilter:
              "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
            WebkitBackdropFilter:
              "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
            minWidth: 0,
            maxWidth: "min(92vw, 860px)",
            pointerEvents: "auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img src={logo} alt="Logo" className="viewer-logo" />
          </Box>
          {system?.name && (
            <Box sx={{ minWidth: 0, textAlign: "left" }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  opacity: 0.72,
                  letterSpacing: "0.12em",
                  lineHeight: 1.1,
                  fontSize: "0.62rem",
                }}
              >
                Experience
              </Typography>
              <Typography
                component="h2"
                sx={{
                  m: 0,
                  lineHeight: 1.15,
                  fontSize: "clamp(0.88rem, 1.35vw, 1.12rem)",
                  fontWeight: 650,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "min(58vw, 720px)",
                }}
              >
                {system.name}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <AttributionBox />
    </Box>
  );
};

export default ViewerHeader;
